import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  UserIcon, 
  PhoneIcon, 
  EnvelopeIcon,
  CurrencyDollarIcon,
  ClockIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { navigateBack } from '../components/RoutePersistence';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const TransportationAuthorization = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { makeAuthenticatedRequest } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const [driver, setDriver] = useState(null);
  const [billingDetails, setBillingDetails] = useState(null);
  const [locations, setLocations] = useState([]);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);

  useEffect(() => {
    loadJobDetails();
  }, [jobId]);

  // Redirect to appointment details page instead of using this separate authorization page
  useEffect(() => {
    if (jobId) {
      navigate(`/appointments/${jobId}`);
    }
  }, [jobId, navigate]);

  const loadJobDetails = async () => {
    try {
      setLoading(true);
      const response = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs/${jobId}`);
      
      if (!response || typeof response.json !== 'function') {
        throw new Error('Invalid response received');
      }
      
      const data = await response.json();
      
      if (data.success) {
        setJob(data.data.job);
        setDriver(data.data.driver);
        setBillingDetails(data.data.billingDetails);
        setLocations(data.data.locations || []);
      } else {
        toast.error(data.message || 'Failed to load job details');
        navigate('/appointments');
      }
    } catch (error) {
      console.error('Error loading job details:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load job details'));
      navigate('/appointments');
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = async () => {
    try {
      setSubmitting(true);
      const response = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs/${jobId}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorized: true
        })
      });

      if (!response || typeof response.json !== 'function') {
        throw new Error('Invalid response received');
      }

      const data = await response.json();
      
      if (data.success) {
        // Check if already authorized
        if (data.alreadyAuthorized) {
          toast.success(data.message);
        } else {
          toast.success('Transportation job authorized successfully!');
        }
        navigate('/appointments');
      } else {
        toast.error(data.message || 'Failed to authorize job');
      }
    } catch (error) {
      console.error('Error authorizing job:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to authorize job'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason.trim()) {
      toast.error('Please provide a reason for declining');
      return;
    }

    try {
      setSubmitting(true);
      const response = await makeAuthenticatedRequest(`${API_BASE}/customer/transportation-jobs/${jobId}/authorize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          authorized: false,
          declineReason: declineReason
        })
      });

      if (!response || typeof response.json !== 'function') {
        throw new Error('Invalid response received');
      }

      const data = await response.json();
      
      if (data.success) {
        toast.success('Transportation job declined. We will find an alternative solution.');
        navigate('/appointments');
      } else {
        toast.error(data.message || 'Failed to decline job');
      }
    } catch (error) {
      console.error('Error declining job:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to decline job'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading job details...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Job Not Found</h2>
          <p className="text-gray-600 mb-4">The transportation job could not be found.</p>
          <button
            onClick={() => navigateBack(navigate, location.pathname, '/appointments')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            title="Go back to previous page"
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-blue-600 px-6 py-4">
            <h1 className="text-2xl font-bold text-white">Transportation Authorization Required</h1>
            <p className="text-blue-100 mt-1">Please review and authorize your transportation request</p>
          </div>

          <div className="p-6">
            {/* Job Details */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Appointment Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Date & Time</p>
                    <p className="font-medium">
                      {(() => {
                        const dateStr = job.scheduled_date;
                        if (dateStr.includes('-') && !dateStr.includes('T')) {
                          const [year, month, day] = dateStr.split('-').map(Number);
                          return new Date(year, month - 1, day).toLocaleDateString();
                        }
                        return new Date(dateStr).toLocaleDateString();
                      })()} at {job.scheduled_time}
                    </p>
                  </div>
                </div>
                <div className="flex items-center">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Trip Type</p>
                    <p className="font-medium capitalize">{job.trip_type?.replace('_', ' ')}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <UserIcon className="h-5 w-5 text-gray-400 mr-3" />
                  <div>
                    <p className="text-sm text-gray-500">Appointment Type</p>
                    <p className="font-medium capitalize">{job.appointment_type?.replace('_', ' ') || 'Transportation'}</p>
                  </div>
                </div>
                {job.wait_time && (
                  <div className="flex items-center">
                    <ClockIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm text-gray-500">Wait Time</p>
                      <p className="font-medium">{job.wait_time} minutes</p>
                    </div>
                  </div>
                )}
              </div>
              
              {job.description && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Description:</strong> {job.description}
                  </p>
                </div>
              )}
              
              {job.special_requirements && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    <strong>Special Requirements:</strong> {job.special_requirements}
                  </p>
                </div>
              )}
            </div>

            {/* Location Details */}
            {locations.length > 0 && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Location Details</h2>
                <div className="space-y-4">
                  {locations.map((location, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-start">
                        <MapPinIcon className="h-5 w-5 text-blue-600 mr-3 mt-1" />
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 capitalize">
                            {location.location_type === 'pickup' ? 'Pickup Location' : 
                             location.location_type === 'dropoff' ? 'Dropoff Location' : 
                             `Location ${location.leg_sequence || index + 1}`}
                          </h3>
                          
                          {location.name && (
                            <p className="text-gray-700 font-medium mt-1">{location.name}</p>
                          )}
                          
                          <div className="mt-2 space-y-1">
                            {location.formatted_address && (
                              <p className="text-gray-600">{location.formatted_address}</p>
                            )}
                            
                            {!location.formatted_address && (
                              <>
                                {location.address && <p className="text-gray-600">{location.address}</p>}
                                {(location.city || location.state || location.zip_code) && (
                                  <p className="text-gray-600">
                                    {[location.city, location.state, location.zip_code]
                                      .filter(Boolean)
                                      .join(', ')}
                                  </p>
                                )}
                              </>
                            )}
                            
                            {location.contact_person && (
                              <div className="flex items-center text-sm text-gray-600 mt-2">
                                <UserIcon className="h-4 w-4 mr-2" />
                                <span>Contact: {location.contact_person}</span>
                              </div>
                            )}
                            
                            {location.phone && (
                              <div className="flex items-center text-sm text-gray-600">
                                <PhoneIcon className="h-4 w-4 mr-2" />
                                <span>{location.phone}</span>
                              </div>
                            )}
                            
                            {location.special_instructions && (
                              <div className="mt-2 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                                <p className="text-sm text-yellow-800">
                                  <strong>Special Instructions:</strong> {location.special_instructions}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Driver Information */}
            {driver && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Assigned Driver</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-start">
                    <UserIcon className="h-8 w-8 text-blue-600 mr-4 mt-1" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">{driver.first_name} {driver.last_name}</h3>
                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-600">
                          <PhoneIcon className="h-4 w-4 mr-2" />
                          <span>{driver.phone}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-600">
                          <EnvelopeIcon className="h-4 w-4 mr-2" />
                          <span>{driver.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {!driver && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Driver Assignment</h2>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-blue-900">
                    A driver will be assigned once you authorize this transportation service.
                  </p>
                </div>
              </div>
            )}

            {/* Billing Information */}
            {billingDetails && (
              <div className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Details</h2>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Miles:</span>
                        <span className="font-medium">{billingDetails.totalMiles || job.calculated_mileage || 'N/A'} miles</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Mileage Rate:</span>
                        <span className="font-medium">${(parseFloat(billingDetails.mileageRate) || 0).toFixed(2)}/mile</span>
                      </div>
                      {billingDetails.waitTime && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Wait Time:</span>
                            <span className="font-medium">{billingDetails.waitTime} minutes</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Wait Time Rate:</span>
                            <span className="font-medium">${(parseFloat(billingDetails.waitTimeRate) || 0).toFixed(2)}/hour</span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(billingDetails.loadFee && billingDetails.loadFee > 0) && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Load Fee:</span>
                          <span className="font-medium">${(parseFloat(billingDetails.loadFee) || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {(billingDetails.flatRate && billingDetails.flatRate > 0) && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Flat Rate:</span>
                          <span className="font-medium">${(parseFloat(billingDetails.flatRate) || 0).toFixed(2)}</span>
                        </div>
                      )}
                      {billingDetails.baseRate && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Base Rate:</span>
                          <span className="font-medium">${(parseFloat(billingDetails.baseRate) || 0).toFixed(2)}</span>
                        </div>
                      )}
                      <div className="border-t pt-2">
                        <div className="flex justify-between text-lg font-semibold">
                          <span className="text-gray-900">Total Amount:</span>
                          <span className="text-blue-600">${(parseFloat(billingDetails.totalEstimate) || 0).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={handleAuthorize}
                disabled={submitting}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircleIcon className="h-5 w-5 mr-2" />
                {submitting ? 'Authorizing...' : 'Authorize Transportation'}
              </button>
              
              <button
                onClick={() => setShowDeclineForm(true)}
                disabled={submitting}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircleIcon className="h-5 w-5 mr-2" />
                Decline Transportation
              </button>
            </div>

            {/* Decline Form */}
            {showDeclineForm && (
              <div className="mt-6 p-4 bg-red-50 rounded-lg border border-red-200">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Decline Transportation</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for declining:
                  </label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="Please provide a reason for declining this transportation request..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows={3}
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleDecline}
                    disabled={submitting || !declineReason.trim()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submitting ? 'Declining...' : 'Confirm Decline'}
                  </button>
                  <button
                    onClick={() => setShowDeclineForm(false)}
                    disabled={submitting}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransportationAuthorization;
